import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { PageHero } from '../layout/PageHero';
import { TopicIndex } from '../layout/TopicIndex';
import { StatementSection } from '../layout/StatementSection';
import { DemoCenterLink } from '../demoCenter/DemoCenterLink';
import { ApplicationPageContent } from '../../data/applicationPages';
import type { DemoDomain } from '../../data/demoCenter';
import { fadeUp } from '../../utils/motion';
type ApplicationPageProps = {
  content: ApplicationPageContent;
  /** Replaces the default hero; the page's own intro is then shown below it. */
  hero?: React.ReactNode;
  /** Links to this solution's planned workflow in the Noetivra Demo Center. */
  demoDomainId?: DemoDomain['id'];
};
export function ApplicationPage({
  content,
  hero,
  demoDomainId
}: ApplicationPageProps) {
  const {
    highlights
  } = content;
  return <>
      {hero ?? <PageHero eyebrow="Applications" title={content.title} lead={content.lead} body={content.intro} backLink={{
      href: '/solutions',
      label: 'All solutions'
    }} data-id="element-89" data-mp="2mv1jx7bs6oby" />}

      {highlights && <section className="bg-surface-bg border-b border-line py-14 lg:py-16" data-id="element-90" data-mp="dgb3702qsfot">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-91" data-mp="dif5jdkzt6mz">
            <motion.h2 {...fadeUp} className="text-sm font-medium text-ink-muted mb-6" data-id="element-92" data-mp="8ij7duyz89fc">
              {highlights.title}
            </motion.h2>
            <motion.ul {...fadeUp} className={`grid grid-cols-1 sm:grid-cols-2 gap-px bg-line border border-line rounded-lg overflow-hidden ${highlights.items.length > 3 ? 'lg:grid-cols-6' : 'md:grid-cols-3'}`} data-id="element-93" data-mp="115vklej8z4vz">
              {highlights.items.map(item => <li key={item.title} className="bg-white p-6" data-id="element-94" data-mp="1owj7b5xtnz2l">
                  <p className="font-heading font-bold text-ink-dark leading-snug" data-id="element-95" data-mp="1k4rgcuar7rbx">{item.title}</p>
                  {item.description && <p className="mt-2 text-ink-muted leading-relaxed" data-id="element-96" data-mp="2321e7sv2elxa">{item.description}</p>}
                </li>)}
            </motion.ul>
          </div>
        </section>}

      {demoDomainId && <DemoCenterLink domainId={demoDomainId} data-id="element-97" data-mp="1u8fya675yyma" />}

      <TopicIndex topics={content.topics} indexLabel="On this page" data-id="element-98" data-mp="1y9rskp0zg80t" />

      <StatementSection title={content.why.title} body={content.why.body} data-id="element-99" data-mp="1kai99ghhcxsm">
        <a href="/solutions" className="mt-8 inline-flex items-center gap-2 text-navy font-semibold hover:text-navy-light transition-colors duration-150 whitespace-nowrap" data-id="element-100" data-mp="1k9769ivuzu2k">
          Explore other solutions
          <ArrowRight size={16} aria-hidden="true" data-id="element-101" data-mp="1fb5mevqlzqq1" />
        </a>
      </StatementSection>
    </>;
}